import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Card, CardContent, CardMedia, FormControlLabel, Switch, Typography} from "@mui/material";
import ReactECharts from "echarts-for-react";
import {useDispatch, useSelector} from "react-redux";
import PageLayout from "../components/layout/PageLayout";
import ReportFilters from "../components/reports/ReportFilters";
import barChartPreview from "../assets/reports/bar-chart.svg";
import {fetchCurrencies, selectCurrencies} from "../store/currencySlice";
import {useTranslation} from "react-i18next";

const formatLabel = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {day: "numeric", month: "short"}).format(date);
};

const toNumber = (value) => Number(value || 0);

const buildWaterfallPoints = (series) => {
    const days = series?.days || [];
    if (!days.length) {
        return [];
    }
    const points = [];
    const firstDay = days[0];
    points.push({
        label: `${formatLabel(firstDay.date)} opening`,
        xLabel: formatLabel(firstDay.date),
        dateLabel: formatLabel(firstDay.date),
        from: 0,
        to: toNumber(firstDay.openingBalance),
        type: "opening"
    });
    days.forEach((day, index) => {
        const opening = toNumber(day.openingBalance);
        const income = toNumber(day.income);
        const expense = Math.abs(toNumber(day.expense));
        const afterIncome = opening + income;
        const afterExpense = afterIncome - expense;
        points.push({
            label: `${formatLabel(day.date)} income`,
            xLabel: "",
            dateLabel: formatLabel(day.date),
            from: opening,
            to: afterIncome,
            type: "income"
        });
        points.push({
            label: `${formatLabel(day.date)} expense`,
            xLabel: "",
            dateLabel: formatLabel(day.date),
            from: afterIncome,
            to: afterExpense,
            type: "expense"
        });
        const nextDay = days[index + 1];
        const isLastDay = index === days.length - 1;
        const nextOpening = nextDay ? toNumber(nextDay.openingBalance) : toNumber(day.closingBalance);
        points.push({
            label: isLastDay ? "Period end" : `${formatLabel(nextDay?.date)} opening`,
            xLabel: isLastDay ? formatLabel(day.date) : formatLabel(nextDay?.date),
            dateLabel: isLastDay ? formatLabel(day.date) : formatLabel(nextDay?.date),
            from: 0,
            to: nextOpening,
            type: "opening"
        });
    });
    return points;
};

const buildWaterfallOption = (series, currencyShortName) => {
    const points = buildWaterfallPoints(series);
    const labels = points.map((point) => point.xLabel || "");
    const colorsByType = {
        opening: "#1976d2",
        income: "#2e7d32",
        expense: "#d32f2f"
    };
    const customData = points.map((point, index) => ({
        value: [index, point.from, point.to],
        itemStyle: {color: colorsByType[point.type] || "#1976d2"}
    }));
    const fmt = (value) => Number(value || 0).toLocaleString(undefined, {maximumFractionDigits: 2});
    const currencySuffix = currencyShortName ? ` ${currencyShortName}` : "";
    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => {
                const index = params?.[0]?.dataIndex ?? 0;
                const point = points[index];
                if (!point) return "";
                const amount = point.to - point.from;
                const valueLine = point.type === "opening"
                    ? `Balance: ${fmt(point.to)}${currencySuffix}`
                    : point.type === "income"
                        ? `Income: ${fmt(Math.abs(amount))}${currencySuffix}`
                        : `Expense: ${fmt(Math.abs(amount))}${currencySuffix}`;
                return [point.dateLabel || "", valueLine].join("<br/>");
            }
        },
        grid: {
            left: 40,
            right: 20,
            top: 40,
            bottom: 80
        },
        xAxis: {
            type: "category",
            data: labels,
            axisLabel: {
                interval: "auto",
                rotate: 35
            }
        },
        yAxis: {
            type: "value"
        },
        series: [
            {
                name: "Daily flow",
                type: "custom",
                data: customData,
                renderItem: (_params, api) => {
                    const categoryIndex = api.value(0);
                    const fromValue = api.value(1);
                    const toValue = api.value(2);
                    const fromCoord = api.coord([categoryIndex, fromValue]);
                    const toCoord = api.coord([categoryIndex, toValue]);
                    const columnWidth = api.size([1, 0])[0] * 0.6;
                    const rect = {
                        x: fromCoord[0] - columnWidth / 2,
                        y: Math.min(fromCoord[1], toCoord[1]),
                        width: columnWidth,
                        height: Math.max(1, Math.abs(toCoord[1] - fromCoord[1]))
                    };
                    return {
                        type: "rect",
                        shape: rect,
                        style: api.style()
                    }
                }
            }
        ]
    };
};

const ReportsCashFlowWaterfallPage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currencies = useSelector((state) => selectCurrencies(state));
    const [groupByCurrency, setGroupByCurrency] = useState(false);
    const [reportResponses, setReportResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        if (!currencies) {
            dispatch(fetchCurrencies());
        }
    }, [currencies, dispatch]);

    const currencyMap = useMemo(() => {
        const list = currencies || [];
        return new Map(list.map((currency) => [String(currency.id), currency.shortName || currency.code || ""]));
    }, [currencies]);

    const formatPeriodRange = useCallback((period) => {
        const from = period?.dateFrom ? new Date(period.dateFrom) : null;
        const to = period?.dateTo ? new Date(period.dateTo) : null;
        if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
            return t("common.labels.period");
        }
        const formatter = new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
        return `${formatter.format(from)} - ${formatter.format(to)}`;
    }, []);

    const handleApply = useCallback(async (request) => {
        const rawPeriods = request?.periods || [];
        if (!rawPeriods.length) {
            setLoadError(t("reports.messages.selectPeriodRange"));
            setReportResponses([]);
            return;
        }
        setIsLoading(true);
        setLoadError(null);
        try {
            const responseList = await Promise.all(
                rawPeriods.map(async (period, index) => {
                    const response = await fetch("/api/reports/cash-flow", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            ...request,
                            groupByCurrency,
                            periods: [period]
                        })
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to load report (${response.status})`);
                    }
                    const json = await response.json();
                    return {
                        key: `period-${index}`,
                        label: formatPeriodRange(period),
                        data: json
                    };
                })
            );
            setReportResponses(responseList);
        } catch (error) {
            console.log(error);
            setLoadError(error?.message || t("reports.messages.failedToLoadReport"));
            setReportResponses([]);
        } finally {
            setIsLoading(false);
        }
    }, [formatPeriodRange, groupByCurrency]);

    const handleReset = useCallback(() => {
        setReportResponses([]);
        setLoadError(null);
    }, []);

    const seriesOptions = useMemo(() => {
        return reportResponses.map((response) => ({
            key: response.key,
            label: response.label,
            series: (response.data?.series || []).map((entry) => ({
                key: entry.key || entry.name,
                name: entry.name,
                displayName: currencyMap.get(String(entry.currencyId))
                    ? `${entry.name} (${currencyMap.get(String(entry.currencyId))})`
                    : entry.name,
                option: buildWaterfallOption(entry, currencyMap.get(String(entry.currencyId)))
            }))
        }));
    }, [currencyMap, reportResponses]);

    return (
        <PageLayout title={t("pages.reports.cashFlow.waterfallTitle")}>
            <CardContent sx={{minHeight: 200, maxHeight: "90%", overflowY: "auto"}}>
                <ReportFilters
                    showTypeTabs={false}
                    showCategories={false}
                    showBudgets={false}
                    includeReportTypeFilter={false}
                    onApply={handleApply}
                    onReset={handleReset}
                />
                <FormControlLabel
                    sx={{mb: 2}}
                    control={
                        <Switch
                            checked={groupByCurrency}
                            onChange={(event) => setGroupByCurrency(event.target.checked)}
                            color="primary"
                        />
                    }
                    label={t("reports.groupByCurrency")}
                />
                {isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loading")}
                    </Typography>
                )}
                {loadError && (
                    <Typography variant="body2" color="error" sx={{mb: 2}}>
                        {loadError}
                    </Typography>
                )}
                {seriesOptions.length ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 3, width: "100%"}}>
                        {seriesOptions.map((group) => (
                            <Box key={group.key} sx={{display: "flex", flexDirection: "column", gap: 2, width: "100%"}}>
                                <Typography variant="h6" sx={{textAlign: "center"}}>
                                    {group.label}
                                </Typography>
                                {group.series.map((entry) => (
                                    <Card
                                        key={`${group.key}-${entry.key}`}
                                        sx={{maxWidth: 1100, width: "100%", margin: "0 auto"}}
                                    >
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{mb: 1}}>
                                                {entry.displayName}
                                            </Typography>
                                            <Box sx={{height: 520, width: "100%"}}>
                                                <ReactECharts option={entry.option} style={{height: "100%", width: "100%"}} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Card sx={{maxWidth: 1000, margin: "0 auto"}}>
                        <CardMedia
                            component="img"
                            image={barChartPreview}
                            alt={t("pages.reports.cashFlow.cards.waterfall.alt")}
                        />
                    </Card>
                )}
            </CardContent>
        </PageLayout>
    );
};

export default ReportsCashFlowWaterfallPage;
